import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Info } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { Reveal, SectionHead, Eyebrow } from "../components/site/Motion";
import { CTAButton } from "../components/site/CTAButton";
import { CTABand } from "../components/site/CTABand";
import { SOLUTIONS, MODELS, SOLUTIONS_DISCLAIMER } from "../content/solutions";
import { IMG } from "../content/site";
import { SolutionFinder } from "../components/site/SolutionFinder";

const SolutionBlock = ({ s, i }) => (
  <article id={s.id} className="scroll-mt-28 border-t border-[#E5E1D3] py-16 md:py-24" data-testid={`solution-section-${s.id}`}>
    <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-20 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <Reveal className="relative aspect-[4/3] overflow-hidden rounded-2xl">
        <img src={s.image} alt={`${s.title} installation`} loading="lazy" className="h-full w-full object-cover" />
        <span className="font-display absolute left-5 top-4 text-6xl font-bold text-white/90 md:text-7xl">{s.n}</span>
      </Reveal>
      <div>
        <Reveal><Eyebrow>{s.kicker}</Eyebrow></Reveal>
        <Reveal delay={0.05}><h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#082619]">{s.title}</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 text-base md:text-lg leading-relaxed text-[#4C6053]">{s.summary}</p></Reveal>
        <Reveal delay={0.15}>
          <ul className="mt-8 space-y-3">
            {s.points.map((p) => (
              <li key={p} className="flex gap-3 text-sm md:text-base text-[#082619]">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#74DC26]"><Check className="h-3 w-3" /></span>{p}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap gap-2">
            {s.models.map((m) => <span key={m} className="rounded-full border border-[#082619]/20 px-3 py-1 text-xs font-medium text-[#082619]">{m}</span>)}
          </div>
        </Reveal>
      </div>
    </div>
    <div className="mt-14 grid gap-10 border-t border-dashed border-[#E5E1D3] pt-12 lg:grid-cols-12 lg:gap-14">
      <Reveal className="lg:col-span-6">
        <p className="eyebrow !text-[0.62rem] text-[#3f8a0f]">In detail</p>
        {(s.detail ?? []).map((d, k) => (
          <p key={k} className="mt-4 text-base leading-relaxed text-[#4C6053]" data-testid={`solution-detail-${s.id}-${k}`}>{d}</p>
        ))}
      </Reveal>
      <Reveal className="lg:col-span-3" delay={0.08}>
        <p className="eyebrow !text-[0.62rem] text-[#3f8a0f]">How it typically works</p>
        <ol className="mt-4 space-y-4">
          {(s.steps ?? []).map((st, k) => (
            <li key={st} className="flex gap-3 text-sm leading-relaxed text-[#082619]">
              <span className="font-mono text-xs text-[#3f8a0f] pt-0.5">0{k + 1}</span>{st}
            </li>
          ))}
        </ol>
      </Reveal>
      <Reveal className="space-y-4 lg:col-span-3" delay={0.16}>
        <div className="rounded-xl bg-[#082619] p-5">
          <p className="eyebrow !text-[0.6rem] text-[#74DC26]">Often suited to</p>
          <p className="mt-2 text-sm leading-relaxed text-[#F9F8F3]">{s.suits}</p>
        </div>
        <div className="rounded-xl bg-[#F2EFE7] p-5">
          <p className="eyebrow !text-[0.6rem] text-[#3f8a0f]">What it depends on</p>
          <p className="mt-2 text-sm leading-relaxed text-[#4C6053]">{s.considerations}</p>
        </div>
      </Reveal>
    </div>
  </article>
);

const ModelCompare = () => {
  const [tab, setTab] = useState("capex");
  const m = MODELS[tab];
  return (
    <section className="relative bg-[#082619] py-24 md:py-32 grain" data-testid="capex-opex-section">
      <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead tone="light" eyebrow="Commercial models" title={["CAPEX or", "OPEX / RESCO?"]} intro="Rooftop and some off-site projects can be structured under different commercial models. The right one depends on your capital priorities, site tenure and risk preferences." />
            <div className="mt-10 inline-flex rounded-full border border-white/20 p-1" role="tablist" data-testid="capex-opex-switch">
              {Object.values(MODELS).map((x) => (
                <button key={x.id} role="tab" aria-selected={tab === x.id} onClick={() => setTab(x.id)} data-testid={`model-tab-${x.id}`}
                  className={`relative rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${tab === x.id ? "text-[#082619]" : "text-[#F9F8F3]"}`}>
                  {tab === x.id && <motion.span layoutId="model-pill" className="absolute inset-0 rounded-full bg-[#74DC26]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
                  <span className="relative">{x.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <motion.div key={tab} initial={{ opacity: 0.4, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="rounded-2xl bg-[#0c3322] p-6 md:p-10" data-testid="model-panel">
              <h3 className="font-display text-3xl font-medium text-[#F9F8F3]">{m.title}</h3>
              <p className="mt-2 text-[#B4C7BA]">{m.lead}</p>
              <dl className="mt-8 divide-y divide-white/10">
                {m.rows.map(([k, v]) => (
                  <div key={k} className="grid gap-1 py-4 sm:grid-cols-[180px_1fr] sm:gap-6">
                    <dt className="eyebrow !text-[0.62rem] text-[#74DC26]">{k}</dt>
                    <dd className="text-sm md:text-base text-[#F9F8F3]">{v}</dd>
                  </div>
                ))}
              </dl>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function SolarSolutionsPage() {
  return (
    <>
      <Seo title="Solar Solutions" path="/solutions" description="Rooftop solar, open access, third-party open access, captive, group captive solar and wind-solar hybrid for C&I consumers in India, with CAPEX and OPEX/RESCO models explained." />
      <PageHero
        eyebrow="Solar solutions"
        title={["Solar models for", <span className="font-serif-i text-[#3f8a0f]">commercial & industrial</span>, "energy consumers."]}
        intro="An overview of the main ways C&I consumers in India procure solar power, what each involves and what it depends on."
        image={IMG.plant}
        imageAlt="Solar panels installed over manufacturing plant facilities"
      >
        <nav className="mt-8 flex flex-wrap gap-2" aria-label="Solutions">
          {SOLUTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} data-testid={`solutions-jump-${s.id}`} className="rounded-full border border-[#082619]/20 px-3 py-1.5 text-xs font-medium text-[#082619] transition-colors hover:bg-[#082619] hover:text-[#F9F8F3]">{s.title}</a>
          ))}
          <a href="#finder" data-testid="solutions-jump-finder" className="rounded-full bg-[#74DC26] px-3 py-1.5 text-xs font-semibold text-[#082619] transition-colors hover:bg-[#082619] hover:text-[#74DC26]">Try the Solution Finder</a>
        </nav>
      </PageHero>
      <SolutionFinder />
      <section className="bg-[#F9F8F3] pt-10" data-testid="solutions-detail">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          {SOLUTIONS.map((s, i) => <SolutionBlock key={s.id} s={s} i={i} />)}
        </div>
      </section>
      <ModelCompare />
      <section className="bg-[#F9F8F3] py-20" data-testid="solutions-disclaimer">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal className="flex flex-col gap-6 rounded-2xl border border-[#E5E1D3] bg-white p-8 md:flex-row md:items-center md:justify-between md:p-10">
            <div className="flex gap-4">
              <Info className="h-6 w-6 shrink-0 text-[#3f8a0f]" strokeWidth={1.5} />
              <p className="max-w-3xl text-sm md:text-base leading-relaxed text-[#4C6053]">{SOLUTIONS_DISCLAIMER}</p>
            </div>
            <CTAButton to="/contact" variant="forest" testId="solutions-disclaimer-cta" className="shrink-0">Get an Assessment</CTAButton>
          </Reveal>
        </div>
      </section>
      <CTABand title={["Not sure which model", "fits your facility?"]} />
    </>
  );
}
